<template>
    <div class="tk-insight-draft">
        <q-banner rounded class="bg-amber-1 text-brown-9 q-mb-lg">
            Publishing makes this insight immutable and visible to authorised engagement users.
        </q-banner>

        <section class="tk-insight-draft__section">
            <div class="tk-insight-draft__label"><label for="insight-overview">Overview</label></div>
            <q-input
                id="insight-overview"
                class="tk-insight-draft__input"
                :model-value="modelValue.overview"
                outlined
                autogrow
                type="textarea"
                counter
                maxlength="3000"
                @update:model-value="updateOverview"
            />
        </section>

        <section
            v-for="(section, index) in modelValue.sections"
            :key="section.name"
            :class="['tk-insight-draft__section', { 'tk-insight-draft__section--suggested': isSuggested(section.name) }]"
        >
            <div class="tk-insight-draft__label">
                <label :for="`insight-section-${section.name}`">
                    <q-icon v-if="isSuggested(section.name)" name="auto_awesome" size="17px" />
                    {{ sectionLabel(section.name) }}
                </label>
                <span v-if="isSuggested(section.name)">AI suggestions — not documented actions</span>
            </div>
            <q-input
                :id="`insight-section-${section.name}`"
                class="tk-insight-draft__input"
                :model-value="section.narrative"
                outlined
                autogrow
                type="textarea"
                counter
                maxlength="8000"
                @update:model-value="updateSectionNarrative(index, $event)"
            />
        </section>

        <section class="tk-insight-draft__coverage">
            <strong>Source coverage</strong>
            <span>{{ modelValue.source_coverage }}</span>
        </section>

        <q-card-actions align="right">
            <q-btn
                flat
                no-caps
                label="Close"
                :disable="saving || publishing"
                data-test="close-insight"
                @click="$emit('close')"
            />
            <q-btn
                outline
                no-caps
                color="primary"
                label="Save draft"
                :loading="saving"
                :disable="publishing"
                data-test="save-insight"
                @click="$emit('save')"
            />
            <q-btn
                unelevated
                no-caps
                color="primary"
                icon="publish"
                label="Save and publish"
                :loading="publishing"
                :disable="saving || !modelValue.overview.trim()"
                data-test="publish-insight"
                @click="$emit('publish')"
            />
        </q-card-actions>
    </div>
</template>

<script>
import { INSIGHT_SECTION_DEFINITIONS } from "@/services/util/engagementInsights";

export default {
    name: "TkEngagementInsightDraft",
    emits: ["close", "publish", "save", "update:modelValue"],
    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        saving: {
            type: Boolean,
            required: true,
        },
        publishing: {
            type: Boolean,
            required: true,
        },
    },
    methods: {
        sectionDefinition(name) {
            return INSIGHT_SECTION_DEFINITIONS.find(definition => definition.name === name);
        },
        sectionLabel(name) {
            return this.sectionDefinition(name)?.label || name;
        },
        isSuggested(name) {
            return !!this.sectionDefinition(name)?.suggested;
        },
        updateOverview(overview) {
            this.$emit("update:modelValue", { ...this.modelValue, overview });
        },
        updateSectionNarrative(index, narrative) {
            const sections = this.modelValue.sections.map((section, sectionIndex) => (
                sectionIndex === index ? { ...section, narrative } : section
            ));
            this.$emit("update:modelValue", { ...this.modelValue, sections });
        },
    },
};
</script>

<style scoped>
.tk-insight-draft {
    padding: 20px 24px 0;
}

.tk-insight-draft__section {
    display: grid;
    grid-template-columns: 170px minmax(0, 1fr);
    gap: 22px;
    padding: 22px 0;
    border-bottom: 1px solid #e1e6e2;
}

.tk-insight-draft__section--suggested {
    margin-top: 22px;
    padding: 22px 18px;
    border: 1px solid #eadbbf;
    border-radius: 8px;
    background: #fbf5e9;
}

.tk-insight-draft__label label,
.tk-insight-draft__coverage strong {
    display: block;
    color: #45544d;
    font-size: 0.83rem;
    font-weight: 600;
    line-height: 1.4;
}

.tk-insight-draft__label span {
    display: block;
    margin-top: 5px;
    color: #80602f;
    font-size: 0.75rem;
}

.tk-insight-draft__input :deep(.q-field__native) {
    color: #35453e;
    font-family: inherit;
    font-size: 0.94rem;
    line-height: 1.65;
}

.tk-insight-draft__coverage {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 18px 0;
    color: #68756f;
    font-size: 0.82rem;
}

@media (max-width: 700px) {
    .tk-insight-draft {
        padding: 14px 14px 0;
    }

    .tk-insight-draft__section {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}
</style>
